import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyEmail, getInvestorHome } from '../api/portalApi';
import { saveInvestorToken } from '../utils/auth';
import { consumeReturnTo } from '../utils/returnTo';
import { track } from '../utils/analytics';

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(t('verify.noToken'));
      return;
    }

    let cancelled = false;

    verifyEmail(token)
      .then(async (res) => {
        if (cancelled) return;
        saveInvestorToken(res.token);
        track('email-verified');
        setStatus('success');
        try {
          const profile = await getInvestorHome();
          if (!cancelled) {
            setTimeout(() => {
              if (!profile.investor.kycCompleted) {
                navigate('/onboarding');
              } else {
                navigate(consumeReturnTo() ?? '/dashboard');
              }
            }, 1500);
          }
        } catch {
          if (!cancelled) setTimeout(() => navigate('/dashboard'), 1500);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus('error');
        setMessage(err instanceof Error ? err.message : t('verify.expired'));
      });

    return () => { cancelled = true; };
  }, [token, navigate, t]);

  return (
    <main className="investor-login-shell">
      <div className="investor-login-logo">
        <img src="/logos/nomadme_sourcing_trading_mark.png" alt="nomadme" />
      </div>
      <p className="investor-login-brand">NomadMe</p>

      <section className="investor-login-card" style={{ textAlign: 'center' }}>
        {status === 'pending' && (
          <>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>⏳</div>
            <h2 style={{ color: '#f1f5f9' }}>{t('verify.verifying')}</h2>
          </>
        )}
        {status === 'success' && (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>✅</div>
            <h2 style={{ color: '#c8a06a' }}>{t('verify.verified')}</h2>
            <p style={{ color: '#94a3b8' }}>{t('verify.redirecting')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>❌</div>
            <h2 style={{ color: '#f1f5f9' }}>{t('verify.failed')}</h2>
            <p style={{ color: '#94a3b8', marginBottom: 24 }}>{message}</p>
            <Link to="/login" className="investor-login-btn" style={{ display: 'inline-block', textDecoration: 'none' }}>
              {t('verify.backToLogin')}
            </Link>
          </>
        )}
      </section>
    </main>
  );
};

export default VerifyEmailPage;
